import {createContext} from 'react';
import {AuthUser, JWT} from 'aws-amplify/auth';

// Session snapshot shape. The provider in src/providers/AuthProvider.tsx
// owns the state-fetching effects; this file only declares the context
// instance + type so consumers (hooks, components) don't import the
// provider's runtime.
// FNP-1501: whether the session has been RESOLVED yet, which `user` alone
// cannot express.
//
// ⚠️ THIS IS THE FIELD A ROUTE GUARD NEEDS, and its absence is a defect the
// platform has filed twice — FNP-1114 against flexi-email-app and FNP-1416
// against flexinet-console-app. `user` starts undefined and is STILL undefined
// when the session resolves to "nobody is signed in", so a guard reading only
// `user` cannot tell "still checking" from "not authenticated". It redirects
// on the first render, before Amplify has answered, and a signed-in user is
// bounced to the landing page on every hard refresh.
//
// 'resolving' until both effects settle, then 'authenticated' or 'anonymous'.
export type AuthStatus = 'resolving' | 'authenticated' | 'anonymous';

export type AuthContextType = {
    user: AuthUser | undefined;
    // ⚠️ Read this, not `user`, when deciding whether to redirect.
    status: AuthStatus;
    idToken?: JWT | undefined;
    accessToken?: JWT | undefined;
    cognitoGroups?: string[] | undefined;
    role?: string | undefined;
};

// ⚠️ The default is 'resolving', not 'anonymous'. A component rendered outside
// the provider must not be told nobody is signed in — that is a claim this
// context cannot make without the provider having run.
export const AuthContext = createContext<AuthContextType>({
    user: undefined,
    status: 'resolving',
});
